import { create, StoreApi, UseBoundStore } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

type Theme = 'light' | 'dark';

interface ThemeState {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

function applyThemeClass(theme: Theme) {
  try {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  } catch (e) {
    console.warn('[themeStore] Failed to apply theme class:', e);
  }
}

// Safe localStorage wrapper
const safeStorage = createJSONStorage(() => ({
  getItem: (name: string) => {
    try {
      return localStorage.getItem(name);
    } catch (e) {
      console.warn(`[themeStore] Failed to read localStorage key "${name}":`, e);
      return null;
    }
  },
  setItem: (name: string, value: string) => {
    try {
      localStorage.setItem(name, value);
    } catch (e) {
      console.warn(`[themeStore] Failed to write localStorage key "${name}":`, e);
    }
  },
  removeItem: (name: string) => {
    try {
      localStorage.removeItem(name);
    } catch (e) {
      console.warn(`[themeStore] Failed to remove localStorage key "${name}":`, e);
    }
  },
}));

function createThemeStore(): UseBoundStore<StoreApi<ThemeState>> {
  try {
    return create<ThemeState>()(
      persist(
        (set) => ({
          theme: 'light',
          toggleTheme: () =>
            set((state) => {
              const next: Theme = state.theme === 'light' ? 'dark' : 'light';
              applyThemeClass(next);
              return { theme: next };
            }),
          setTheme: (theme) => {
            applyThemeClass(theme);
            set({ theme });
          },
        }),
        {
          name: 'wl-theme',
          storage: safeStorage,
          onRehydrateStorage: () => (state) => {
            if (state) applyThemeClass(state.theme);
          },
        }
      )
    );
  } catch (err) {
    console.error('[themeStore] initialization failed:', err);
    return create<ThemeState>()((set) => ({
      theme: 'light',
      toggleTheme: () => set((state) => ({ theme: state.theme === 'light' ? 'dark' : 'light' })),
      setTheme: (theme) => set({ theme }),
    }));
  }
}

export const useThemeStore = createThemeStore();
